/**
 * Chip multi-select for physical items shipped with a case.
 *
 * The list below is the lab's canonical receiving-checklist. Each item is
 * a free-form string so labs can also type their own (e.g. "patient's old
 * crown") via the "Add custom" affordance.
 *
 * Stored as `attached_materials: string[]` on the case doc — the lab uses
 * this list when the courier bag arrives to confirm nothing is missing.
 */
import { useState } from "react";
import { Plus, X } from "lucide-react";

export const COMMON_MATERIALS = [
    "Impression (PVS / silicone)",
    "Impression (alginate)",
    "Bite registration",
    "Opposing model / cast",
    "Articulator / face-bow mounting",
    "Shade tab",
    "Shade photo (printed)",
    "Wax-up",
    "Custom tray",
    "Old crown / prosthesis",
    "X-ray / CBCT print",
    "Preparation guide",
    "Patient mounting",
    "Try-in framework",
];

export default function AttachedMaterialsPicker({ value = [], onChange }) {
    const [custom, setCustom] = useState("");

    const toggle = (item) => {
        if (value.includes(item)) {
            onChange(value.filter((x) => x !== item));
        } else {
            onChange([...value, item]);
        }
    };

    const addCustom = () => {
        const c = custom.trim();
        if (!c) return;
        if (value.includes(c)) {
            setCustom("");
            return;
        }
        onChange([...value, c]);
        setCustom("");
    };

    return (
        <div data-testid="attached-materials-picker" className="space-y-2.5">
            {/* Common materials — quick-tick chips */}
            <div className="flex flex-wrap gap-2">
                {COMMON_MATERIALS.map((item) => {
                    const selected = value.includes(item);
                    return (
                        <button
                            key={item}
                            type="button"
                            data-testid={`material-chip-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => toggle(item)}
                            className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition ${
                                selected
                                    ? "bg-[#0B1120] text-cyan-300 border-[#0B1120]"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                            }`}
                        >
                            {item}
                        </button>
                    );
                })}
            </div>

            {/* Custom item — for things outside the canonical list */}
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            addCustom();
                        }
                    }}
                    data-testid="material-custom-input"
                    placeholder="Add custom item (press Enter)…"
                    className="tr-input flex-1 text-[13px]"
                />
                <button
                    type="button"
                    onClick={addCustom}
                    disabled={!custom.trim()}
                    data-testid="material-custom-add"
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-slate-900 text-white text-[12.5px] hover:bg-slate-800 disabled:opacity-40"
                >
                    <Plus size={12} /> Add
                </button>
            </div>

            {/* Custom selected items (those NOT in COMMON_MATERIALS) — shown
                as removable pills so the user can drop a typo without re-typing. */}
            {value.filter((v) => !COMMON_MATERIALS.includes(v)).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                    {value.filter((v) => !COMMON_MATERIALS.includes(v)).map((custom) => (
                        <span
                            key={custom}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-800 text-[12px] border border-cyan-200"
                        >
                            {custom}
                            <button
                                type="button"
                                onClick={() => onChange(value.filter((x) => x !== custom))}
                                data-testid={`material-custom-remove-${custom.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="text-cyan-600 hover:text-cyan-900"
                            >
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
